import { useLayoutEffect, useRef, useState } from "react";
import Logo from "@web-ui/components/Logo";
import { RemoveScroll } from "react-remove-scroll";
import ThemeButton from "@web-ui/components/ThemeButton";
import NavLink from "@web-ui/components/NavLink";
import Button from "@web-ui/components/Button";
import { usePathname } from "@web-ui/adapters/navigation";
import Dropdown from "./Dropdown";

import type { NavigationItem } from "@web-ui/types/navigation";

/*
 * The sidebar. The active item's pill is one element, the rail, that slides between the top level items
 * with a spring (motion.css .motion-rail; owner 2026-10-13) instead of a pill mounted on each item in
 * turn. The rail measures the current item (aria-current="page") after paint and on resize; until it has,
 * the item draws its own static pill, so the server rendered page is complete and the hand over is
 * invisible. A nested item (a Dropdown's list) keeps its own pill.
 */
type SidebarProps = {
    navigation: NavigationItem[];
    navigationLabel?: string;
    closeLabel?: string;
    /** The theme switch's accessible names, for apps that translate. */
    themeLabels?: { dark?: string; light?: string };
    brand?: React.ReactNode;
    /**
     * Drawn at the top instead of the logo link: something that is not a link itself, e.g. the Studio Console's brand
     * switcher (a menu cannot sit inside a link). When set, `brand` is not used.
     */
    top?: React.ReactNode;
    visibleSidebar?: boolean;
    hideSidebar?: boolean;
    onCloseSidebar?: () => void;
};

const Sidebar = ({
    navigation,
    navigationLabel = "Main",
    closeLabel = "Close menu",
    themeLabels,
    brand,
    top,
    visibleSidebar,
    hideSidebar,
    onCloseSidebar,
}: SidebarProps) => {
    const pathname = usePathname();
    const nav = useRef<HTMLElement>(null);
    const [rail, setRail] = useState<{ top: number; height: number } | null>(null);
    const [measured, setMeasured] = useState(false);

    useLayoutEffect(() => {
        const element = nav.current;
        if (!element) return;
        const measure = () => {
            const active = element.querySelector<HTMLElement>('[data-rail-item][aria-current="page"]');
            const next = active ? { top: active.offsetTop, height: active.offsetHeight } : null;
            // Same place, same state: apps rebuild `navigation` on every render, so this must not loop.
            setRail((current) => (current?.top === next?.top && current?.height === next?.height ? current : next));
            setMeasured(true);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [pathname, navigation]);

    return (
        <div
            className={`fixed top-0 start-0 bottom-0 flex flex-col w-85 p-5 bg-b-surface1 transition-transform duration-(--motion-slow) ease-(--ease-out-quint) max-4xl:w-70 max-3xl:w-60 max-xl:w-74 max-md:p-3 ${
                visibleSidebar
                    ? `${
                          hideSidebar
                              ? "z-40 translate-x-0"
                              : "max-xl:z-40 max-xl:translate-x-0"
                      }`
                    : `${
                          hideSidebar
                              ? "z-40 -translate-x-full rtl:translate-x-full"
                              : "max-xl:z-40 max-xl:-translate-x-full max-xl:rtl:translate-x-full"
                      }`
            }`}
        >
            {top ? <div className={`mb-5 ${hideSidebar ? "pe-14" : "max-xl:pe-14"}`}>{top}</div> : <Logo className="mb-5">{brand}</Logo>}
            <Button
                className={`group absolute top-5 end-5 max-md:top-3 max-md:end-3 ${
                    hideSidebar ? "flex" : "!hidden max-xl:!flex"
                }`}
                icon="close"
                onClick={onCloseSidebar}
                aria-label={closeLabel}
                isCircle
                isWhite
            />
            <RemoveScroll
                className="flex flex-col grow overflow-auto -mx-5 px-5 scrollbar scrollbar-thumb-t-tertiary/50 scrollbar-track-b-surface2 max-md:-mx-3 max-md:px-3"
                enabled={visibleSidebar}
            >
                <nav ref={nav} className="relative flex flex-col gap-1 shrink-0" aria-label={navigationLabel}>
                    {rail ? (
                        <span
                            className="motion-rail absolute top-0 start-0 end-0 gradient-menu rounded-full shadow-depth-menu pointer-events-none"
                            style={{ transform: `translateY(${rail.top}px)`, height: rail.height }}
                            aria-hidden="true"
                        >
                            <span className="absolute inset-0.25 bg-b-pop rounded-full"></span>
                        </span>
                    ) : null}
                    {navigation.map((item, index) => (
                        <div className="contents" key={item.title}>
                            {item.group && item.group !== navigation[index - 1]?.group ? (
                                <div className={`px-3 pb-2 text-overline uppercase text-t-tertiary ${index === 0 ? "" : "pt-5"}`}>{item.group}</div>
                            ) : null}
                            {item.href ? <NavLink value={{ ...item, href: item.href }} pill={!measured} railItem /> : <Dropdown value={item} />}
                        </div>
                    ))}
                </nav>
            </RemoveScroll>
            <div className="mt-auto pt-6 max-md:pt-4">
                <ThemeButton
                    className={`${hideSidebar ? "hidden max-lg:block" : ""}`}
                    darkLabel={themeLabels?.dark}
                    lightLabel={themeLabels?.light}
                />
            </div>
        </div>
    );
};

export default Sidebar;
